/**
 * The Baueingabe-Check-KAi lockup: the Velox-KAi mark, then the product's type lockup.
 *
 * Per the brand spec: the mark at 24px (the canonical asset, used as is — see
 * `BrandMark`), followed by "Baueingabe-Check-KAi · von Velox-KAi" in the display face.
 * The type treatment is unchanged from the type-only lockup this replaced; the
 * brand's navy and green now come from the mark, which is where they belong.
 * The company wordmark is deliberately NOT set as two-tone type anywhere: the
 * green `#22A06B` is a fill colour, not an AA-safe text colour on white
 * (`--color-signal-green` vs `--color-signal-green-text` in index.css), and the
 * mark already carries the brand's colour on both light and dark ground.
 */
import { BrandMark } from '@/components/BrandMark'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'

export function Wordmark({ className }: { className?: string }) {
  const { t } = useI18n()

  // No aria-label here: the accessible name IS the visible text
  // ("Baueingabe-Check-KAi von Velox-KAi"), which is also what a real wordmark would need
  // as its alt text. An aria-label on a plain <span> is a prohibited attribute
  // (axe: aria-prohibited-attr). The mark is aria-hidden for the same reason —
  // it would otherwise announce "Velox-KAi" a second time.
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <BrandMark />
      <span className="flex items-baseline gap-2">
        <span className="font-display text-[17px] leading-none font-semibold tracking-[-0.02em] text-ink sm:text-[20px]">
          Baueingabe-Check-KAi
        </span>
        {/* Hidden below `sm`: on a 390 px viewport the attribution is what
            pushed the lockup onto a second header row, and the same sentence
            stands in the footer of every page. */}
        <span className="hidden text-[12px] leading-none font-medium text-muted sm:inline">
          {t('app.attribution')}
        </span>
      </span>
    </span>
  )
}
